import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { Menu, X, LogOut, ChevronDown } from "lucide-react";

const aboutItems = [
  { hash: "#leadership", label: "Leadership & Governance" },
  { hash: "#vision", label: "Vision, Mission & Core Principle" },
  { hash: "#fraud", label: "How TMF Prevents Fraud" },
];

const links = [
  { to: "/programmes", label: "Programmes" },
  { to: "/check", label: "Check Your TMF Benefit" },
  { to: "/find-vendor", label: "Find a Vendor" },
  { to: "/help", label: "Help Centre" },
  { to: "/partners", label: "Partners" },
];

export function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);

  const PORTAL_PATHS = {
    beneficiary: "/beneficiary", sponsor: "/sponsor", vendor: "/vendor",
    state_coordinator: "/coordinator", finance: "/finance", admin: "/admin", super_admin: "/admin",
  };
  const portalPath = PORTAL_PATHS[user?.role] || null;

  const goAbout = (hash) => { setAboutOpen(false); setOpen(false); navigate(`/about${hash}`); };

  return (
    <header className="sticky top-0 z-50 glass border-b border-emerald-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-home-logo"><Logo /></Link>

        <nav className="hidden lg:flex items-center gap-7">
          <a href="/" data-testid="nav-link-home" onClick={(e) => { e.preventDefault(); navigate("/"); }}
            className="text-sm font-medium text-emerald-950/80 hover:text-emerald-600 transition-colors">Home</a>

          <div className="relative" onMouseEnter={() => setAboutOpen(true)} onMouseLeave={() => setAboutOpen(false)}>
            <button data-testid="nav-about-trigger" onClick={() => navigate("/about")}
              className="flex items-center gap-1 text-sm font-medium text-emerald-950/80 hover:text-emerald-600 transition-colors">
              About Us <ChevronDown className="w-4 h-4" />
            </button>
            {aboutOpen && (
              <div className="absolute left-0 top-full pt-2 w-64" data-testid="nav-about-menu">
                <div className="rounded-xl border border-emerald-100 bg-white shadow-xl p-2">
                  {aboutItems.map((a) => (
                    <button key={a.hash} data-testid={`nav-about-${a.hash.replace("#", "")}`} onClick={() => goAbout(a.hash)}
                      className="w-full text-left px-3 py-2.5 rounded-lg text-sm text-emerald-950/80 hover:bg-emerald-50 hover:text-emerald-700 transition-colors">
                      {a.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {links.map((l) => (
            <a key={l.label} href={l.to} data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={(e) => { e.preventDefault(); navigate(l.to); }}
              className="text-sm font-medium text-emerald-950/80 hover:text-emerald-600 transition-colors">
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          {portalPath ? (
            <>
              <Button variant="ghost" data-testid="nav-my-portal-btn" onClick={() => navigate(portalPath)} className="text-emerald-800">My Portal</Button>
              <Button variant="outline" data-testid="nav-logout-btn" onClick={() => { logout(); navigate("/"); }}><LogOut className="w-4 h-4 mr-1.5" /> Logout</Button>
            </>
          ) : (
            <>
              <Button variant="ghost" data-testid="nav-login-btn" onClick={() => navigate("/login")} className="text-emerald-800">Login</Button>
              <Button data-testid="nav-register-btn" onClick={() => navigate("/register")} className="bg-emerald-600 hover:bg-emerald-700 rounded-full px-5">Register</Button>
            </>
          )}
        </div>

        <button className="lg:hidden p-2" data-testid="nav-mobile-toggle" onClick={() => setOpen(!open)}>
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-emerald-100 bg-white px-4 py-4 space-y-1">
          <a href="/" className="block py-2.5 text-sm font-medium text-emerald-950" onClick={(e) => { setOpen(false); e.preventDefault(); navigate("/"); }}>Home</a>
          <div className="py-1">
            <div className="text-[11px] font-mono uppercase tracking-widest text-emerald-600 py-1">About Us</div>
            {aboutItems.map((a) => (
              <button key={a.hash} onClick={() => goAbout(a.hash)} className="block w-full text-left py-2 pl-3 text-sm text-emerald-950/80">{a.label}</button>
            ))}
          </div>
          {links.map((l) => (
            <a key={l.label} href={l.to} className="block py-2.5 text-sm font-medium text-emerald-950" onClick={(e) => { setOpen(false); e.preventDefault(); navigate(l.to); }}>{l.label}</a>
          ))}
          <div className="pt-3 flex flex-col gap-2">
            {portalPath ? (
              <>
                <Button onClick={() => { setOpen(false); navigate(portalPath); }} className="bg-emerald-600 w-full">My Portal</Button>
                <Button variant="outline" onClick={() => { logout(); setOpen(false); navigate("/"); }} className="w-full">Logout</Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => { setOpen(false); navigate("/login"); }} className="w-full">Login</Button>
                <Button onClick={() => { setOpen(false); navigate("/register"); }} className="bg-emerald-600 w-full">Register</Button>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
